import moment from 'moment'
import docendo from '../docendo'

const LESSON_CALENDAR_TYPES = ['klass', 'club']

export default {
  data () {
    return {
      events: [],
      isLoading: false,
      startDate: null,
      endDate: null,
      reportEventTypeFilter: null,
      readEventsRequestId: 0
    }
  },
  computed: {
    schoolId () {
      return this.$route.params.school_id * 1
    },
    user () {
      return this.$store.state.user
    },
    schoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    schoolYearStartDate () {
      return this.schoolYear ? this.schoolYear.student_time_start : null
    },
    schoolYearEndDate () {
      return this.schoolYear ? this.schoolYear.student_time_end : null
    },
    isDatesPristine () {
      return this.compareDates(this.schoolYearStartDate, this.startDate) && this.compareDates(this.schoolYearEndDate, this.endDate)
    },
    reportCalendars () {
      return this.$store.getters.userCalendars || []
    },
    eventsInRange () {
      const startDate = moment(this.startDate).startOf('day')
      const endDate = moment(this.endDate).endOf('day')
      return this.events.filter(event => {
        const start = moment(event.start)
        return start.isSameOrAfter(startDate) && start.isSameOrBefore(endDate)
      })
    },
    absenceEventsInRange () {
      return this.eventsInRange.filter(event => event.event_type === 'absence')
    },
    lessonsInRange () {
      return this.eventsInRange.filter(event => event.event_type !== 'absence' && this.getLessonKlasses(event).length > 0)
    },
    absenceEventsByCalId () {
      const hash = {}
      this.absenceEventsInRange.forEach(event => {
        if (!hash[event.calendar_id]) {
          hash[event.calendar_id] = []
        }
        hash[event.calendar_id].push({
          event,
          startMs: moment(event.start).valueOf(),
          endMs: moment(event.end).valueOf()
        })
      })
      return hash
    },
    // Every lesson where a teacher is absent or a substitute has been added
    affectedLessons () {
      const affected = []
      this.lessonsInRange.forEach(lesson => {
        const startMs = moment(lesson.start).valueOf()
        const endMs = moment(lesson.end).valueOf()
        const teachers = this.getLessonTeachers(lesson)
        const substitutes = this.getLessonSubstitutes(lesson)
        const absentTeachers = []
        const absenceReasons = []
        teachers.forEach(teacher => {
          const absences = (this.absenceEventsByCalId[teacher.calendar_id] || [])
            .filter(absence => absence.startMs < endMs && absence.endMs > startMs)
          if (absences.length > 0) {
            absentTeachers.push(teacher)
            absences.forEach(absence => {
              if (absenceReasons.indexOf(absence.event.title) < 0) {
                absenceReasons.push(absence.event.title)
              }
            })
          }
        })
        if (absentTeachers.length < 1 && substitutes.length < 1) {
          return
        }
        affected.push({
          lesson,
          minutes: this.getLessonMinutes(lesson),
          klasses: this.getLessonKlasses(lesson),
          teachers,
          absentTeachers,
          substitutes,
          absenceReasons,
          hasCoTeacher: teachers.length > absentTeachers.length,
          hasTeacherNotes: this.hasText(lesson.teacher_notes),
          coverage: this.getCoverageCategory(lesson, absentTeachers, substitutes, teachers)
        })
      })
      return affected
    }
  },
  watch: {
    user () {
      this.$store.dispatch('readSchoolYears', this.schoolId)
      this.$store.dispatch('getSchoolCalendars', this.schoolId)
    },
    reportCalendars () {
      this.readEvents()
    },
    schoolYearEndDate () {
      this.setDates(this.schoolYearStartDate, this.schoolYearEndDate)
    }
  },
  methods: {
    resetDates () {
      this.setDates(this.schoolYearStartDate, this.schoolYearEndDate)
    },
    setStartDate (date) {
      this.setDates(date, null)
    },
    setEndDate (date) {
      this.setDates(null, date)
    },
    setDates (start, end) {
      if (start) {
        this.startDate = start
      }
      if (end) {
        this.endDate = end
      }
    },
    compareDates (firstDate, secondDate) {
      return moment(firstDate).format('YYYY-MM-DD') === moment(secondDate).format('YYYY-MM-DD')
    },
    readEvents () {
      const calendarIds = this.reportCalendars.map(calendar => calendar.id)
      if (calendarIds.length < 1 || !this.schoolYearStartDate) {
        return
      }
      const requestId = ++this.readEventsRequestId
      this.isLoading = true
      this.fetchEvents(calendarIds, this.schoolYearStartDate, this.schoolYearEndDate, this.reportEventTypeFilter, events => {
        if (requestId !== this.readEventsRequestId) {
          return
        }
        this.events = Object.freeze(events)
        this.isLoading = false
      })
    },
    fetchEvents (calendarIds, start, end, eventTypeFilter, callback) {
      docendo.calendarEvents2(calendarIds, start, end, eventTypeFilter,
        success => {
          const events = success && success.length > 0 ? JSON.parse(success) : []
          const seen = {}
          callback(events.filter(event => {
            if (event.school_id !== this.schoolId || seen[event.event_id]) {
              return false
            }
            seen[event.event_id] = true
            return true
          }))
        }, failure => {
          console.log('failure', failure)
          callback([])
        })
    },
    getLessonKlasses (lesson) {
      return (lesson.calendars || []).filter(cal => LESSON_CALENDAR_TYPES.indexOf(cal.calendar_type) > -1)
    },
    getLessonTeachers (lesson) {
      return (lesson.calendars || []).filter(cal => cal.calendar_type === 'user' && !cal.is_substitute && !cal.substitute)
    },
    getLessonSubstitutes (lesson) {
      return (lesson.calendars || []).filter(cal => cal.calendar_type === 'user' && (cal.is_substitute || cal.substitute))
    },
    getLessonMinutes (lesson) {
      return moment(lesson.end).diff(moment(lesson.start), 'minutes')
    },
    getCoverageCategory (lesson, absentTeachers, substitutes, teachers) {
      if (lesson.canceled) {
        return 'canceled'
      }
      if (lesson.away) {
        return 'away'
      }
      if (substitutes.length > 0) {
        return 'substitute'
      }
      if (teachers.length > absentTeachers.length) {
        return 'coTeacher'
      }
      return 'uncovered'
    },
    getGradeName (klassName) {
      const name = (klassName || '').trim()
      const digits = name.match(/^(\d+)/)
      if (digits) {
        return `${digits[1] * 1}. årgang`
      }
      if (/^(bh|0)/i.test(name)) {
        return '0. årgang'
      }
      return 'Øvrige'
    },
    hasText (html) {
      return !!html && html.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ').trim().length > 0
    },
    formatHours (minutes) {
      return (minutes / 60).toFixed(1).replace('.', ',')
    },
    formatPercent (part, total) {
      if (!total) {
        return '-'
      }
      return Math.round(part / total * 100) + ' %'
    }
  },
  beforeMount () {
    this.$store.dispatch('getUser')
  },
  mounted () {
    this.setDates(this.schoolYearStartDate, this.schoolYearEndDate)
  }
}



// WEBPACK FOOTER //
// ./src/mixins/schoolDataMixin.js